import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ProjectAsset,
  UserAssetConstraints,
  deleteProjectAsset,
  fetchProjectAssetObjectUrl,
  getUserAssetConstraints,
  listProjectAssets,
  uploadProjectAsset,
} from '../services/projectAssets';
import { actionFailureMessage } from '../utils/actionFailure';
import { formatAudioSummary } from '../utils/assetFiles';
import AssetUploadForm, { AssetUpload } from './AssetUploadForm';
import AudioPreview from './AudioPreview';
import { IconMusic, IconSpeaker } from './Icons';
import InlineConfirm from './InlineConfirm';
import InlineError from './InlineError';

interface ProjectAssetsPanelProps {
  projectId: string;
}

export default function ProjectAssetsPanel({ projectId }: ProjectAssetsPanelProps) {
  const { t } = useTranslation(['common']);
  const [assets, setAssets] = useState<ProjectAsset[]>([]);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const [constraints, setConstraints] = useState<UserAssetConstraints | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingRemoval, setConfirmingRemoval] = useState<string | null>(null);
  const thumbnailsRef = useRef<Record<string, string>>({});

  const loadAssets = useCallback(async () => {
    const loaded = await listProjectAssets(projectId);
    setAssets(loaded);

    for (const asset of loaded) {
      if (asset.assetType.toLowerCase() !== 'image') continue;
      if (thumbnailsRef.current[asset.assetId]) continue;
      try {
        const url = await fetchProjectAssetObjectUrl(projectId, asset.assetId);
        thumbnailsRef.current = { ...thumbnailsRef.current, [asset.assetId]: url };
        setThumbnails(thumbnailsRef.current);
      } catch {
        // Thumbnail is decorative; the asset row still renders without it.
      }
    }
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    Promise.all([loadAssets(), getUserAssetConstraints().then(setConstraints)])
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
      Object.values(thumbnailsRef.current).forEach((url) => URL.revokeObjectURL(url));
      thumbnailsRef.current = {};
    };
  }, [loadAssets]);

  const handleUpload = async ({ name, description, file, options }: AssetUpload) => {
    await uploadProjectAsset(projectId, name, description, file, options);
    setIsAdding(false);
    await loadAssets();
  };

  const handleDelete = async (asset: ProjectAsset) => {
    setConfirmingRemoval(null);
    setError(null);
    try {
      await deleteProjectAsset(projectId, asset.assetId);
      const url = thumbnailsRef.current[asset.assetId];
      if (url) {
        URL.revokeObjectURL(url);
        const { [asset.assetId]: _removed, ...rest } = thumbnailsRef.current;
        thumbnailsRef.current = rest;
        setThumbnails(rest);
      }
      setAssets((prev) => prev.filter((a) => a.assetId !== asset.assetId));
    } catch (e) {
      setError(actionFailureMessage(e, t('assets.deleteFailed', 'Failed to remove the asset.')));
    }
  };

  return (
    <section
      style={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        minHeight: 0,
        flex: 1,
        background: 'var(--bg-2)',
      }}
    >
      <div style={{ padding: '16px 24px 8px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span className="eyebrow">{t('assets.title', 'Your assets')}</span>
        <div style={{ flex: 1 }} />
        {constraints && (
          <span className="mono muted" style={{ fontSize: 11 }}>
            {assets.length} / {constraints.maxAssetsPerProject}
          </span>
        )}
      </div>

      <div className="muted" style={{ padding: '0 24px 12px', fontSize: 12, lineHeight: 1.5 }}>
        {t(
          'assets.hint',
          'Optional — add your own images, fonts, sound effects and music. Entire generates images for you, but using your own saves credits and keeps your style. Reference one in chat by typing / followed by its name.',
        )}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '0 24px 16px', minHeight: 0 }}>
        {isLoading ? (
          <div className="muted" style={{ fontSize: 13 }}>
            {t('assets.loading', 'Loading…')}
          </div>
        ) : assets.length === 0 && !isAdding ? (
          <div className="muted" style={{ fontSize: 13 }}>
            {t('assets.empty', 'No assets uploaded for this project yet.')}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {assets.map((asset) => (
              <div
                key={asset.assetId}
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center',
                  padding: 10,
                  border: '1px solid var(--line-2)',
                  borderRadius: 10,
                  background: 'var(--surface)',
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    flex: 'none',
                    borderRadius: 8,
                    border: '1px solid var(--line)',
                    overflow: 'hidden',
                    display: 'grid',
                    placeItems: 'center',
                    background: 'var(--bg)',
                  }}
                >
                  {asset.audio ? (
                    asset.audio.kind === 'Music' ? (
                      <IconMusic size={22} role="img" aria-label={t('assets.audio.music', 'Music')} />
                    ) : (
                      <IconSpeaker size={22} role="img" aria-label={t('assets.audio.soundEffect', 'Sound effect')} />
                    )
                  ) : thumbnails[asset.assetId] ? (
                    <img
                      src={thumbnails[asset.assetId]}
                      alt={asset.name}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <span className="muted" style={{ fontSize: 10 }}>
                      {asset.fileExtension.replace('.', '').toUpperCase()}
                    </span>
                  )}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="mono" style={{ fontSize: 13 }}>/{asset.name}</div>
                  <div
                    className="muted"
                    style={{
                      fontSize: 12,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={asset.description}
                  >
                    {asset.description}
                  </div>
                  {asset.audio && (
                    <>
                      <div className="mono muted" style={{ fontSize: 11, marginTop: 2 }}>
                        {formatAudioSummary(asset.audio, t)}
                      </div>
                      <AudioPreview projectId={projectId} assetId={asset.assetId} name={asset.name} />
                    </>
                  )}
                </div>
                {confirmingRemoval === asset.assetId ? (
                  <InlineConfirm
                    question={t('assets.confirmDelete', 'Remove from this project?')}
                    confirmLabel={t('assets.confirmDeleteYes', 'Yes, remove')}
                    danger
                    onConfirm={() => handleDelete(asset)}
                    onCancel={() => setConfirmingRemoval(null)}
                  />
                ) : (
                  <button
                    type="button"
                    className="btn sm"
                    onClick={() => setConfirmingRemoval(asset.assetId)}
                    title={t('assets.delete', 'Remove from project')}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {error && (
          <div style={{ marginTop: 12 }}>
            <InlineError>{error}</InlineError>
          </div>
        )}

        {isAdding && (
          <div style={{ marginTop: 12 }}>
            <AssetUploadForm
              constraints={constraints}
              existingNames={assets.map((asset) => asset.name)}
              onUpload={handleUpload}
              onCancel={() => setIsAdding(false)}
            />
          </div>
        )}
      </div>

      {!isAdding && (
        <div style={{ padding: '0 24px 20px' }}>
          <button
            type="button"
            className="btn sm"
            style={{ width: '100%', justifyContent: 'center' }}
            onClick={() => {
              setError(null);
              setIsAdding(true);
            }}
            disabled={isLoading || (constraints != null && assets.length >= constraints.maxAssetsPerProject)}
          >
            {t('assets.add', '+ Add asset')}
          </button>
        </div>
      )}
    </section>
  );
}
